import { navigateMediaItem } from '@/helpers/navigateMediaItem';
import { ctaTriggerAndNavigate } from '@/helpers/ctaTriggerAndNavigate';
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { buildMediaAssetIdParam } from '@/helpers/buildMediaAssetIdParam';
import localStorageRepositories from "@/plugins/localStorageRepositories";
import { queryCtaElements } from '@/helpers/ctaShadow';
import { isReelLike } from '@/helpers/isReelLike';

export const clickCtaAction = () => {
  const buttons = queryCtaElements('.ctaFrame');
  const ignoreClickAfterTouchMs = 700;
  const touchMoveThresholdPx = 10; // タップ判定用の移動しきい値
  let lastTouchHandledAt = 0;
  const touchStateByButton = new WeakMap();
  const hasCtaLink = (ctaButton) => Boolean(String(ctaButton?.getAttribute('ctaLink') || '').trim());

  const handleCtaTrigger = (ctaButton) => {
    let contents = navigateMediaItem();
    // リール型・タイル型（PC）ではmoviePositionが0以外の場合にcontentsを補完する
    if (!window.storeeelItems.isMobile && isReelLike(window.storeeelItems.frame)) {
      if (window.moviePosition != 0 && contents == false) {
        contents = window.storeeelItems.contentGroups[window.moviePosition].contents;
        window.moviePosition = 0;
      }
    }
    let ctaUrl = '';
    let mediaId = '';
    let mediaName = '';
    let mediaAssetType = '';
    let isFrequencyInsertContent = false;
    let isFeedFolderContent = false;
    if (window.storeeelItems.isMobile && window.storeeelItems.frame == 'circle') {
      const currentContent = contents[window.moviePosition];
      ctaUrl = currentContent.ctaLink || '';
      mediaId = currentContent.isFrequencyInsertContent
        ? (currentContent.frequencyInsertContentId || '')
        : (currentContent.mediaAsset?.id || '');
      mediaName = currentContent.mediaAsset?.name || '';
      mediaAssetType = currentContent.mediaAsset?.type || '';
      isFrequencyInsertContent = Boolean(currentContent.isFrequencyInsertContent);
      isFeedFolderContent = Boolean(currentContent.isFeedFolderContent);
    } else {
      ctaUrl = ctaButton.getAttribute('ctaLink');
      mediaId = ctaButton.getAttribute('mediaId');
      mediaName = ctaButton.getAttribute('mediaName');
      mediaAssetType = ctaButton.getAttribute('mediaAssetType') || '';
      isFrequencyInsertContent = ctaButton.getAttribute('isFrequencyInsertContent') === 'true';
      isFeedFolderContent = ctaButton.getAttribute('isFeedFolderContent') === 'true';
    }

    if (!ctaUrl) return;

    // CTAイベントを計測
    trackCtaClick(mediaName, mediaId, mediaAssetType, isFrequencyInsertContent);
    const groupId = setLocalStorage(mediaId, mediaAssetType, isFrequencyInsertContent, isFeedFolderContent);

    const crossDomainCvParams = window.storeeelCrossDomainEnabled
      ? {
        campaignId: window.storeeelItems.id,
        mediaId,
        groupId,
        mediaAssetType,
        isFrequencyInsertContent,
        isFeedFolderContent,
      }
      : null;

    ctaTriggerAndNavigate(ctaUrl, crossDomainCvParams)
  };

  buttons.forEach((ctaButton) => {
    if (ctaButton.dataset.storeeelCtaBound === 'true') return;
    ctaButton.dataset.storeeelCtaBound = 'true';

    ctaButton.addEventListener('touchstart', (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      touchStateByButton.set(ctaButton, {
        startX: touch.clientX,
        startY: touch.clientY,
        moved: false,
      });
    }, { passive: true });

    ctaButton.addEventListener('touchmove', (e) => {
      const state = touchStateByButton.get(ctaButton);
      const touch = e.touches?.[0];
      if (!state || !touch || state.moved) return;
      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;
      if (Math.hypot(dx, dy) > touchMoveThresholdPx) {
        state.moved = true;
      }
    }, { passive: true });

    ctaButton.addEventListener('touchend', (e) => {
      const state = touchStateByButton.get(ctaButton);
      touchStateByButton.delete(ctaButton);
      if (state?.moved) return;
      lastTouchHandledAt = Date.now();
      // CTAリンク未設定時は長押し解除など上位touchend処理を阻害しない
      if (!hasCtaLink(ctaButton)) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      handleCtaTrigger(e.currentTarget);
    }, { passive: false });

    ctaButton.addEventListener('touchcancel', () => {
      touchStateByButton.delete(ctaButton);
    }, { passive: true });

    ctaButton.addEventListener('click', (e) => {
      if (Date.now() - lastTouchHandledAt < ignoreClickAfterTouchMs) {
        // touchend直後の合成clickが背面のタップ遷移へ伝播しないようにする
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (!hasCtaLink(ctaButton)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      handleCtaTrigger(e.currentTarget);
    });
  });
};

function trackCtaClick(mediaName, mediaId, mediaAssetType, isFrequencyInsertContent = false) {
  if (isFrequencyInsertContent) {
    const groupId = window.storeeelItems?.frame !== 'circle' ? null
      : (window.storeeelItems?.isMobile ? (window._storeeelLastRealGroupId ?? null) : (window.iconId ?? null));
    trackStoreeelEvent('storeeel_frequency_insert_cta_click', {
      'storeeel_name': mediaName,
      ...buildMediaAssetIdParam(mediaId, mediaAssetType),
      'campaign_id': window.storeeelItems.id,
      ...(groupId != null ? { 'group_id': groupId } : {}),
    });
    return;
  }

  // フィードフォルダも通常の storeeel_cta_click で計測する。mediaId が
  // `{content hashid}:{商品ID}` の複合IDになっており、API側で商品単位に集計される。
  // 通常コンテンツと同じく group_id は送らない（集計は複合IDで行うため許容する）。
  trackStoreeelEvent('storeeel_cta_click', {
    'storeeel_name': mediaName,
    ...buildMediaAssetIdParam(mediaId, mediaAssetType),
    'campaign_id': window.storeeelItems.id,
  });
}

function setLocalStorage(mediaId, mediaAssetType, isFrequencyInsertContent = false, isFeedFolderContent = false) {
  let groupId = null;
  if (isReelLike(window.storeeelItems?.frame)) {
    // リール型・タイル型はグループIDをCVログに記録しない
    groupId = null;
  } else if ((isFrequencyInsertContent || isFeedFolderContent) && window.storeeelItems?.isMobile) {
    // SP circle: プロモーション/フィードフォルダ表示中は window.iconId がスワイパースライドIDに
    // 上書きされるため直前の実グループIDを使用する
    groupId = window._storeeelLastRealGroupId ?? null;
  } else {
    // PC circle: プロモーション/フィードフォルダはグループ内スライドとして注入されるため window.iconId は実グループIDのまま
    groupId = window.iconId ?? null;
  }

  localStorageRepositories.get("ctaClick").set(
    mediaId,
    window.storeeelItems.id,
    groupId,
    mediaAssetType,
    isFrequencyInsertContent,
    isFeedFolderContent
  );

  return groupId;
}
